import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { CreditsService } from 'src/app/services/credits/credits.service';
import { CurrencyService } from 'src/app/services/currency/currency.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  TransferInstructionsDialogComponent,
  TransferInstructionsDialogData,
} from '../transfer-instructions-dialog/transfer-instructions-dialog.component';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardContent,
  MatCardSubtitle,
  MatCardActions,
} from '@angular/material/card';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss'],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    MatCardSubtitle,
    MatCardActions,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientsService);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private currencyService = inject(CurrencyService);
  private creditsService = inject(CreditsService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  private readonly destroy = new Subject<void>();

  readonly form = this.fb.group({
    amount: this.fb.control<string | number | null>(null, [
      Validators.required,
      this.helpersService.formatNumber as ValidatorFn,
      Validators.min(1),
    ]),
    currency_iso: this.fb.control<string | null>(null, Validators.required),
  });

  loading = false;

  get amount() {
    return this.form.controls.amount;
  }
  get currency_iso() {
    return this.form.controls.currency_iso;
  }

  currencyOptions: string[] = [];
  get allowCard(): boolean {
    return this.currency_iso.value === 'HUF';
  }

  async ngOnInit(): Promise<void> {
    const client = await this.clientsService.getSelectedClient();
    this.currency_iso.patchValue(client.credits[0]?.currency_iso ?? 'HUF');
    await this.setCurrencies();

    this.route.queryParams.pipe(takeUntil(this.destroy)).subscribe((params) => {
      const currency = params.currency_iso?.trim();
      if (this.currencyOptions.includes(currency)) {
        this.currency_iso.patchValue(currency);
      }

      const amount = +params.amount?.trim();
      if (Number.isSafeInteger(amount)) {
        this.amount.patchValue(amount);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async onSelectTransferPayment(): Promise<void> {
    if (!this.form.valid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { amount: amountValue, currency_iso } = this.form.getRawValue();
    if (!currency_iso) {
      return;
    }

    const amount = Number(String(amountValue).replace(/\D/g, ''));

    try {
      this.loading = true;
      const credit_transfer_id = await this.creditsService.buyWithTransfer({
        amount,
        currency_iso,
      });

      this.creditsService.refreshTransferHistory.next();

      this.dialog.open<
        TransferInstructionsDialogComponent,
        TransferInstructionsDialogData
      >(TransferInstructionsDialogComponent, {
        data: {
          credit_transfer_id,
          amount,
          currency_iso,
        },
        disableClose: true,
      });
    } catch (error) {
      console.error('Error while buying with card', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      this.loading = false;
    }
  }

  async onSelectCardPayment(): Promise<void> {
    if (!this.form.valid || this.currency_iso.value !== 'HUF') {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { amount: amountValue } = this.form.getRawValue();
    const amount = Number(String(amountValue).replace(/\D/, ''));

    try {
      this.loading = true;
      await this.creditsService.buyWithCard({
        amount,
      });
    } catch (error) {
      console.error('Error while buying with card', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      this.loading = false;
    }
  }

  private async setCurrencies(): Promise<void> {
    try {
      this.currencyOptions = await this.currencyService.getAllowedCurrencies();
    } catch {
      console.error('Error while getting currencies');
    }
  }
}
